import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Indent,
} from "docx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
export const generateHierarchicalWord = async (rows) => {
  try {
    if (!Array.isArray(rows) || rows.length === 0) {
      toast.warn("No data available to export!");
      return;
    }
    // Group data by hierarchy
    const projectsMap = {};
    rows.forEach((row) => {
      const pName = row.ProjectName || "Unassigned Project";
      const wp = row.WP_Title || null;
      const dlv = row.Deliverable_Title || null;
      const task = row.Task_Title || null;

      if (!projectsMap[pName]) {
        projectsMap[pName] = {
          info: {
            Description: row.ProjectDescription,
            Budget: row.ProjectBudget,
            Cost: row.ProjectCost,
            StartDate: row.ProjectStartDate,
            EndDate: row.ProjectEndDate,
            Progress: row.ProjectProgressLevel,
            CreatedBy: row.ProjectCreatedBy,
            AssistantSuperAdmin: row.ProjectAssistantSuperAdmin,
            Documents: row.ProjectDocuments,
          },
          workPackages: {},
        };
      }

      if (wp) {
        if (!projectsMap[pName].workPackages[wp]) {
          projectsMap[pName].workPackages[wp] = {
            info: {
              Description: row.WP_Description,
              Number: row.WP_Number,
              Status: row.WP_Status,
              Cost: row.WP_Cost,
              ApprovalStatus: row.WP_ApprovalStatus,
              RejectionReason: row.WP_RejectionReason,
              Progress: row.WP_ProgressLevel,
              Managers: row.WP_ManagerNames,
              StartDate: row.WP_StartDate,
              EndDate: row.WP_EndDate,
              Attachments: row.WP_Attachments,
            },
            deliverables: {},
            tasksWithoutDeliverable: [],
          };
        }
      }

      if (dlv && wp) {
        if (!projectsMap[pName].workPackages[wp].deliverables[dlv]) {
          projectsMap[pName].workPackages[wp].deliverables[dlv] = {
            info: {
              Description: row.Deliverable_Description,
              Number: row.Deliverable_Number,
              Cost: row.Deliverable_Cost,
              Status: row.Deliverable_Status,
              Progress: row.Deliverable_ProgressLevel,
              AssignedTo: row.Deliverable_AssignedTo,
              CreatedBy: row.Deliverable_CreatedBy,
              StartDate: row.Deliverable_StartDate,
              EndDate: row.Deliverable_EndDate,
              Files: row.Deliverable_Files,
            },
            tasks: [],
          };
        }
      }

      if (task) {
        const taskObj = {
          Title: row.Task_Title,
          Description: row.Task_Description,
          Notes: row.Task_CompletionNotes,
          Approval: row.Task_ApprovalStatus,
          RejectionReason: row.Task_RejectionReason,
          Progress: row.Task_ProgressLevel,
          AssignedTo: row.Task_AssignedTo,
          AssignedBy: row.Task_AssignedBy,
          CreatedBy: row.Task_CreatedBy,
          StartDate: row.Task_StartDate,
          EndDate: row.Task_EndDate,
          Attachments: row.Task_Attachments,
        };

        if (dlv && wp) {
          projectsMap[pName].workPackages[wp].deliverables[dlv].tasks.push(
            taskObj
          );
        } else if (wp) {
          projectsMap[pName].workPackages[wp].tasksWithoutDeliverable.push(
            taskObj
          );
        }
      }
    });

    // Build Word content
    const children = [];
    // Title
    children.push(
      new Paragraph({
        text: "Abarka Projects Detailed Report",
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
      })
    );
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Generated On: ${new Date().toLocaleString("en-US")}`,
            italics: true,
          }),
        ],
        alignment: AlignmentType.RIGHT,
      })
    );
    children.push(new Paragraph(" "));
    // Iterate hierarchy
    for (const [projectName, projectObj] of Object.entries(projectsMap)) {
      // PROJECT
      children.push(
        new Paragraph({
          text: `Project: ${projectName}`,
          heading: HeadingLevel.HEADING_1,
        })
      );

      for (const [label, value] of Object.entries(projectObj.info)) {
        if (value) children.push(new Paragraph(`• ${label}: ${value}`));
      }

      children.push(new Paragraph(" "));

      // WORK PACKAGES
      for (const [wpName, wpObj] of Object.entries(projectObj.workPackages)) {
        children.push(
          new Paragraph({
            text: `Work Package: ${wpName}`,
            heading: HeadingLevel.HEADING_2,
          })
        );

        for (const [label, value] of Object.entries(wpObj.info)) {
          if (value)
            children.push(
              new Paragraph({
                text: `• ${label}: ${value}`,
                indent: { left: 500 },
              })
            );
        }

        // DELIVERABLES
        for (const [dlvName, dlvObj] of Object.entries(wpObj.deliverables)) {
          children.push(
            new Paragraph({
              text: `Deliverable: ${dlvName}`,
              heading: HeadingLevel.HEADING_3,
              indent: { left: 700 },
            })
          );

          for (const [label, value] of Object.entries(dlvObj.info)) {
            if (value)
              children.push(
                new Paragraph({
                  text: `• ${label}: ${value}`,
                  indent: { left: 900 },
                })
              );
          }

          // TASKS under deliverable
          dlvObj.tasks.forEach((task) => {
            children.push(
              new Paragraph({
                text: `Task: ${task.Title}`,
                bullet: { level: 0 },
                indent: { left: 1200 },
              })
            );

            for (const [label, value] of Object.entries(task)) {
              if (label !== "Title" && value)
                children.push(
                  new Paragraph({
                    text: `- ${label}: ${value}`,
                    indent: { left: 1400 },
                  })
                );
            }
          });
        }

        // TASKS without deliverable
        wpObj.tasksWithoutDeliverable.forEach((task) => {
          children.push(
            new Paragraph({
              text: `Task: ${task.Title}`,
              bullet: { level: 0 },
              indent: { left: 900 },
            })
          );

          for (const [label, value] of Object.entries(task)) {
            if (label !== "Title" && value)
              children.push(
                new Paragraph({
                  text: `- ${label}: ${value}`,
                  indent: { left: 1100 },
                })
              );
          }
        });

        children.push(new Paragraph(" "));
      }
    }
    const doc = new Document({
      sections: [{ children }],
    });
    const blob = await Packer.toBlob(doc);
    saveAs(
      blob,
      `Abarka_Project_Report_${new Date()
        .toLocaleDateString("en-US")
        .replace(/\//g, "-")}.docx`
    );
  } catch (error) {
    console.error(error);
  }
};

export const generateProjectsWord = async (formattedData) => {
  if (!formattedData || formattedData.length === 0) return;

  const children = [];

  children.push(
    new Paragraph({
      text: "Abarka – Project Report",
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
    })
  );

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Generated On: ${new Date().toLocaleString("en-US")}`,
          italics: true,
        }),
      ],
      alignment: AlignmentType.RIGHT,
    })
  );

  children.push(new Paragraph(" "));

  formattedData.forEach((p) => {
    children.push(
      new Paragraph({
        text: `Project #${p.SNo}: ${p.ProjectName}`,
        heading: HeadingLevel.HEADING_2,
      })
    );

    Object.entries(p).forEach(([label, value]) => {
      if (label !== "SNo" && label !== "ProjectName") {
        children.push(new Paragraph(`• ${label}: ${value}`));
      }
    });

    children.push(
      new Paragraph("----------------------------------------------------")
    );
  });

  const doc = new Document({
    sections: [{ children }],
  });

  const blob = await Packer.toBlob(doc);

  saveAs(
    blob,
    `Projects_Report_${new Date()
      .toLocaleDateString("en-US")
      .replace(/\//g, "-")}.docx`
  );
};
export const generateWorkPackagesWord = async (formattedData) => {
  if (!formattedData || formattedData.length === 0) return;

  const children = [];

  children.push(
    new Paragraph({
      text: "Abarka – Work Package Report",
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
    })
  );

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Generated On: ${new Date().toLocaleString("en-US")}`,
          italics: true,
        }),
      ],
      alignment: AlignmentType.RIGHT,
    })
  );

  children.push(new Paragraph(" "));

  formattedData.forEach((wp) => {
    children.push(
      new Paragraph({
        text: `Work Package #${wp.SNo}: ${wp.Title}`,
        heading: HeadingLevel.HEADING_2,
      })
    );

    Object.entries(wp).forEach(([label, value]) => {
      if (label !== "SNo" && label !== "Title") {
        children.push(new Paragraph(`• ${label}: ${value}`));
      }
    });

    children.push(
      new Paragraph("----------------------------------------------------")
    );
  });

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);

  saveAs(
    blob,
    `WorkPackages_Report_${new Date()
      .toLocaleDateString("en-US")
      .replace(/\//g, "-")}.docx`
  );
};

export const generateDeliverablesWord = async (formattedData) => {
  if (!formattedData || formattedData.length === 0) return;

  const children = [];

  children.push(
    new Paragraph({
      text: "Abarka – Deliverables Report",
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
    })
  );

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Generated On: ${new Date().toLocaleString("en-US")}`,
          italics: true,
        }),
      ],
      alignment: AlignmentType.RIGHT,
    })
  );

  children.push(new Paragraph(" "));

  formattedData.forEach((d) => {
    children.push(
      new Paragraph({
        text: `Deliverable #${d.SNo}: ${d.Title}`,
        heading: HeadingLevel.HEADING_2,
      })
    );

    Object.entries(d).forEach(([label, value]) => {
      if (label !== "SNo" && label !== "Title") {
        children.push(new Paragraph(`• ${label}: ${value}`));
      }
    });

    children.push(
      new Paragraph("----------------------------------------------------")
    );
  });

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);

  saveAs(
    blob,
    `Deliverables_Report_${new Date()
      .toLocaleDateString("en-US")
      .replace(/\//g, "-")}.docx`
  );
};
export const generateTasksWord = async (formattedData) => {
  if (!formattedData || formattedData.length === 0) return;

  const children = [];

  children.push(
    new Paragraph({
      text: "Abarka – Tasks Report",
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
    })
  );

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Generated On: ${new Date().toLocaleString("en-US")}`,
          italics: true,
        }),
      ],
      alignment: AlignmentType.RIGHT,
    })
  );

  children.push(new Paragraph(" "));

  formattedData.forEach((t) => {
    children.push(
      new Paragraph({
        text: `Task #${t.SNo}: ${t.Title}`,
        heading: HeadingLevel.HEADING_2,
      })
    );

    Object.entries(t).forEach(([label, value]) => {
      if (label !== "SNo" && label !== "Title") {
        children.push(new Paragraph(`• ${label}: ${value}`));
      }
    });

    children.push(
      new Paragraph("----------------------------------------------------")
    );
  });

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);

  saveAs(
    blob,
    `Tasks_Report_${new Date()
      .toLocaleDateString("en-US")
      .replace(/\//g, "-")}.docx`
  );
};
